import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Users, CheckCircle, Clock, Building2, Mail, Phone, MapPin, Loader2, Trash2, Key, Copy, ShoppingCart } from "lucide-react";

const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState({ email: "", password: "", company_name: "" });
  const [resettingPassword, setResettingPassword] = useState({});
  const [showMinPurchaseModal, setShowMinPurchaseModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [minPurchaseValue, setMinPurchaseValue] = useState("");

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (customerId) => {
    setApproving({ ...approving, [customerId]: true });
    try {
      await axios.put(`${API}/admin/customers/${customerId}/approve`);
      toast.success("Customer approved successfully!");
      await fetchCustomers();
    } catch (error) {
      toast.error("Failed to approve customer");
    } finally {
      setApproving({ ...approving, [customerId]: false });
    }
  };

  const handleDelete = async (customerId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete ${companyName}? This action cannot be undone.`)) {
      return;
    }

    setDeleting({ ...deleting, [customerId]: true });
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`);
      toast.success("Customer deleted successfully!");
      await fetchCustomers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete customer";
      toast.error(errorMsg);
    } finally {
      setDeleting({ ...deleting, [customerId]: false });
    }
  };

  const canDeleteCustomer = (customer) => {
    // Can delete if pending OR approved with no purchase count
    return customer.status === 'pending' || (customer.status === 'approved' && (!customer.purchase_count || customer.purchase_count === 0));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleResetPassword = async (customerId, email, companyName) => {
    if (!confirm(`Generate new login credentials for ${companyName}?`)) {
      return;
    }
    
    setResettingPassword({ ...resettingPassword, [customerId]: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/customers/${customerId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show credentials modal
      setGeneratedCredentials({
        email: email,
        password: response.data.temporary_password,
        company_name: companyName
      });
      setShowCredentialsModal(true);
      
      toast.success("New credentials generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword({ ...resettingPassword, [customerId]: false });
    }
  };

  const handleSetMinPurchase = (customer) => {
    setSelectedCustomer(customer);
    setMinPurchaseValue(customer.minimum_purchase?.toString() || "");
    setShowMinPurchaseModal(true);
  };

  const handleSaveMinPurchase = async () => {
    if (!selectedCustomer) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/admin/customers/${selectedCustomer.id}/minimum-purchase`, {
        minimum_purchase: minPurchaseValue ? parseInt(minPurchaseValue) : null
      });
      toast.success("Minimum purchase updated!");
      setShowMinPurchaseModal(false);
      await fetchCustomers();
    } catch (error) {
      toast.error("Failed to update minimum purchase");
    } finally {
      setLoading(false);
    }
  };

  const pendingCustomers = customers.filter(c => c.status === 'pending');
  const approvedCustomers = customers.filter(c => c.status === 'approved');

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return <Badge className="bg-green-600 text-white"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    } else if (status === 'pending') {
      return <Badge className="bg-yellow-600 text-white"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    } else {
      return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-700">{customers.length}</p>
              </div>
              <Users className="w-8 h-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-700">{pendingCustomers.length}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-700">{approvedCustomers.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Customers */}
      {pendingCustomers.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            Pending Approval ({pendingCustomers.length})
          </h3>
          <div className="space-y-4">
            {pendingCustomers.map((customer) => (
              <Card key={customer.id} className="border-l-4 border-l-yellow-500">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-gray-600" />
                        <h4 className="font-semibold text-lg text-gray-800">{customer.company_name}</h4>
                        {getStatusBadge(customer.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 font-semibold">Contact Person</p>
                          <p className="text-gray-800">{customer.first_name} {customer.last_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 font-semibold flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            Email
                          </p>
                          <p className="text-gray-800">{customer.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 font-semibold flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            Phone
                          </p>
                          <p className="text-gray-800">{customer.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 font-semibold">VAT Number</p>
                          <p className="text-gray-800">{customer.vat_number || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="text-sm">
                        <p className="text-gray-600 font-semibold flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Address
                        </p>
                        <p className="text-gray-800">{customer.address || 'N/A'}</p>
                      </div>

                      <div className="text-xs text-gray-500">
                        Registered: {new Date(customer.created_at).toLocaleString()}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(customer.id)}
                        disabled={approving[customer.id]}
                        className="!bg-green-600 hover:!bg-green-700 !text-white"
                      >
                        {approving[customer.id] ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDelete(customer.id, customer.company_name)}
                        disabled={deleting[customer.id]}
                        variant="outline"
                        className="!border-red-300 !text-red-600 hover:!bg-red-50"
                      >
                        {deleting[customer.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Approved Customers */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Approved Customers ({approvedCustomers.length})
        </h3>
        {approvedCustomers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No approved customers yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {approvedCustomers.map((customer) => (
              <Card key={customer.id} className="border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-gray-600" />
                        <h4 className="font-semibold text-lg text-gray-800">{customer.company_name}</h4>
                        {getStatusBadge(customer.status)}
                        {customer.minimum_purchase && (
                          <Badge className="bg-orange-100 text-orange-700 border border-orange-300">
                            Min: {customer.minimum_purchase} tokens
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Contact</p>
                          <p className="text-gray-800">{customer.first_name} {customer.last_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Email</p>
                          <p className="text-gray-800">{customer.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Phone</p>
                          <p className="text-gray-800">{customer.phone || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500">
                        Approved: {new Date(customer.created_at).toLocaleString()}
                        {canDeleteCustomer(customer) && (
                          <span className="text-gray-400 ml-2">• No transactions</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSetMinPurchase(customer)}
                        variant="outline"
                        className="!border-orange-300 !text-orange-600 hover:!bg-orange-50"
                        title="Set minimum purchase requirement"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span className="ml-2 hidden sm:inline">Min Purchase</span>
                      </Button>
                      
                      <Button
                        onClick={() => handleResetPassword(customer.id, customer.email, customer.company_name)}
                        disabled={resettingPassword[customer.id]}
                        variant="outline"
                        className="!border-blue-300 !text-blue-600 hover:!bg-blue-50"
                        title="Generate new login credentials"
                      >
                        {resettingPassword[customer.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Key className="w-4 h-4" />
                        )}
                        <span className="ml-2 hidden sm:inline">Reset Password</span>
                      </Button>
                      
                      {canDeleteCustomer(customer) && (
                        <Button
                          onClick={() => handleDelete(customer.id, customer.company_name)}
                          disabled={deleting[customer.id]}
                          variant="outline"
                          className="!border-red-300 !text-red-600 hover:!bg-red-50"
                          title="Delete customer (no transactions)"
                        >
                          {deleting[customer.id] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Login Credentials</DialogTitle>
            <DialogDescription>
              Generated new credentials for {generatedCredentials.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-2">
                Save these credentials - they won&apos;t be shown again!
              </p>
            </div>
            
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={generatedCredentials.email}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedCredentials.email)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Temporary Password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={generatedCredentials.password}
                    readOnly
                    className="font-mono text-sm bg-yellow-50 border-yellow-300"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedCredentials.password)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Email has been sent to customer with:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Login link</li>
                <li>Email and password</li>
                <li>Instructions to change password</li>
              </ul>
            </div>
            
            <Button
              onClick={() => setShowCredentialsModal(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Minimum Purchase Modal */}
      <Dialog open={showMinPurchaseModal} onOpenChange={setShowMinPurchaseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Minimum Purchase</DialogTitle>
            <DialogDescription>
              Set the minimum number of tokens {selectedCustomer?.company_name} must purchase per order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Minimum Tokens</Label>
              <Input
                type="number"
                min="0"
                value={minPurchaseValue}
                onChange={(e) => setMinPurchaseValue(e.target.value)}
                placeholder="Enter minimum (leave blank for no minimum)"
              />
              <p className="text-xs text-gray-500">
                Leave blank to remove the minimum purchase requirement
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMinPurchaseModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMinPurchase}
                disabled={loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersTab;
